import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Calendar, Clock, MapPin, Phone, User, Filter } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import BookingDetailModal from '../components/BookingDetailModal';
import PageHelp from '../components/help/PageHelp';
import { ANIMAL_LABELS } from '../lib/animals';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VALID_STATUSES = ['ALL', 'PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELED'];

export default function AllBookingsPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = (searchParams.get('status') || 'ALL').toUpperCase();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(
    VALID_STATUSES.includes(initialStatus) ? initialStatus : 'ALL'
  );
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    // keep URL in sync so a refresh preserves the filter
    if (statusFilter === 'ALL') {
      if (searchParams.get('status')) {
        const next = new URLSearchParams(searchParams);
        next.delete('status');
        setSearchParams(next, { replace: true });
      }
    } else if (searchParams.get('status') !== statusFilter) {
      const next = new URLSearchParams(searchParams);
      next.set('status', statusFilter);
      setSearchParams(next, { replace: true });
    }
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (statusFilter !== 'ALL') {
        params.status = statusFilter;
      }
      
      const response = await axios.get(`${API_URL}/api/dashboard/bookings`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBookings(response.data);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      CONFIRMED: 'bg-green-100 text-green-800 border-green-300',
      COMPLETED: 'bg-blue-100 text-blue-800 border-blue-300',
      CANCELED: 'bg-red-100 text-red-800 border-red-300',
    };
    
    return (
      <Badge className={`${variants[status] || ''} border`}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-1">
            <h2 className="text-2xl font-bold">All Bookings</h2>
            <PageHelp
              testId="bookings"
              title="All Bookings"
              purpose="Every appointment you have, in one list. Filter by status at the top."
              actions={[
                { label: 'Status filter', desc: 'show only pending, confirmed, completed, or canceled jobs' },
                { label: 'Tap a booking', desc: 'open it to update details, payment, or notes' },
              ]}
              nextStep="Tap any booking to confirm it or record a payment."
            />
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CANCELED">Canceled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {bookings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                {statusFilter === 'ALL' 
                  ? 'No bookings yet'
                  : `No ${statusFilter.toLowerCase()} bookings`
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((booking) => {
              const startDate = parseISO(booking.startDateTime);
              
              return (
                <Card
                  key={booking.id}
                  data-testid={`booking-${booking.id}`}
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={() => setOpenId(booking.id)}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{booking.service?.name || 'Service'}</CardTitle>
                        <CardDescription className="flex flex-wrap items-center gap-3 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(startDate, 'MMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(startDate, 'h:mm a')}
                          </span>
                        </CardDescription>
                      </div>
                      {getStatusBadge(booking.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-medium">{booking.client?.name}</p>
                        <p className="text-muted-foreground">{booking.client?.phone}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <p className="text-muted-foreground">{booking.jobLocation || booking.locationAddress}</p>
                    </div>

                    {/* Show saved animal records if provided */}
                    {booking.animals && booking.animals.length > 0 && (
                      <div className="text-xs space-y-0.5 mt-1">
                        <p className="text-muted-foreground font-medium">
                          {booking.animals.length} selected animal{booking.animals.length === 1 ? '' : 's'}:
                        </p>
                        {booking.animals.map((a) => (
                          <p key={a.id} className="text-muted-foreground">
                            • <span className="font-medium text-foreground">{a.name}</span>
                            {a.animalType ? ` — ${ANIMAL_LABELS[a.animalType] || a.animalType}` : ''}
                            {a.breed ? ` (${a.breed})` : ''}
                            {a.age != null ? `, ${a.age}y` : ''}
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Show group-booking details */}
                    {(!booking.animals || booking.animals.length === 0) &&
                      booking.animalQuantity &&
                      booking.animalType && (
                        <p className="text-xs text-muted-foreground">
                          Group: <strong className="text-foreground">{booking.animalQuantity}</strong>{' '}
                          {(ANIMAL_LABELS[booking.animalType] || booking.animalType).toLowerCase()}
                          {booking.animalQuantity > 1 ? 's' : ''}
                          {booking.groupDescription ? ` — ${booking.groupDescription}` : ''}
                        </p>
                      )}

                    {/* Fallback: just count */}
                    {(!booking.animals || booking.animals.length === 0) &&
                      !booking.animalQuantity &&
                      booking.horseCount > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Animals: {booking.horseCount}
                        </p>
                      )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <BookingDetailModal
        bookingId={openId}
        open={!!openId}
        onOpenChange={(o) => !o && setOpenId(null)}
        onUpdated={loadBookings}
      />
    </DashboardLayout>
  );
}
